import * as React from 'react';
import Image from '@/components/common/Image';
import logoMark from '@/assets/Vector.svg';

import { IconSvgProps } from '@/types';

type LogoProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'height' | 'width'> & {
  size?: number;
};

export const Logo: React.FC<LogoProps> = ({ size = 36, style, alt, ...rest }) => (
  <Image
    alt={alt ?? 'Turbo Labs logo'}
    height={size}
    src={logoMark}
    style={{ width: size, height: size, ...style }}
    width={size}
    {...rest}
  />
);

export const DiscordIcon: React.FC<IconSvgProps> = ({
  size = 24,
  width,
  height,
  ...props
}) => {
  return (
    <svg
      height={size || height}
      viewBox="0 0 24 24"
      width={size || width}
      {...props}
    >
      <path
        d="M14.82 4.26a10.14 10.14 0 0 0-.53 1.1 14.66 14.66 0 0 0-4.58 0 10.14 10.14 0 0 0-.53-1.1 16 16 0 0 0-4.13 1.3 17.33 17.33 0 0 0-3 11.59 16.6 16.6 0 0 0 5.07 2.59A12.89 12.89 0 0 0 8.23 18a9.65 9.65 0 0 1-1.71-.83 3.39 3.39 0 0 0 .42-.33 11.66 11.66 0 0 0 10.12 0q.21.18.42.33a10.84 10.84 0 0 1-1.71.84 12.41 12.41 0 0 0 1.08 1.78 16.44 16.44 0 0 0 5.06-2.59 17.22 17.22 0 0 0-3-11.59 16.09 16.09 0 0 0-4.09-1.35zM8.68 14.81a1.94 1.94 0 0 1-1.8-2 1.93 1.93 0 0 1 1.8-2 1.93 1.93 0 0 1 1.8 2 1.93 1.93 0 0 1-1.8 2zm6.64 0a1.94 1.94 0 0 1-1.8-2 1.93 1.93 0 0 1 1.8-2 1.92 1.92 0 0 1 1.8 2 1.92 1.92 0 0 1-1.8 2z"
        fill="currentColor"
      />
    </svg>
  );
};

export const GithubIcon: React.FC<IconSvgProps> = ({
  size = 24,
  width,
  height,
  ...props
}) => {
  return (
    <svg
      height={size || height}
      viewBox="0 0 24 24"
      width={size || width}
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M12.026 2c-5.509 0-9.974 4.465-9.974 9.974 0 4.406 2.857 8.145 6.821 9.465.499.09.679-.217.679-.481 0-.237-.008-.865-.011-1.696-2.775.602-3.361-1.338-3.361-1.338-.452-1.152-1.107-1.459-1.107-1.459-.905-.619.069-.605.069-.605 1.002.07 1.527 1.028 1.527 1.028.89 1.524 2.336 1.084 2.902.829.091-.645.351-1.085.635-1.334-2.214-.251-4.542-1.107-4.542-4.93 0-1.087.389-1.979 1.024-2.675-.101-.253-.446-1.268.099-2.64 0 0 .837-.269 2.742 1.021a9.582 9.582 0 0 1 2.496-.336 9.554 9.554 0 0 1 2.496.336c1.906-1.291 2.742-1.021 2.742-1.021.545 1.372.203 2.387.099 2.64.64.696 1.024 1.587 1.024 2.675 0 3.833-2.33 4.675-4.552 4.922.355.308.675.916.675 1.846 0 1.334-.012 2.41-.012 2.737 0 .267.178.577.687.479C19.146 20.115 22 16.379 22 11.974 22 6.465 17.535 2 12.026 2z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
};

export const MoonFilledIcon = ({
  size = 24,
  width,
  height,
  ...props
}: IconSvgProps) => (
  <svg
    aria-hidden="true"
    focusable="false"
    height={size || height}
    role="presentation"
    viewBox="0 0 24 24"
    width={size || width}
    {...props}
  >
    <path
      d="M21.53 15.93c-.16-.27-.61-.69-1.73-.49a8.46 8.46 0 01-1.88.13 8.409 8.409 0 01-5.91-2.82 8.068 8.068 0 01-1.44-8.66c.44-1.01.13-1.54-.09-1.76s-.77-.55-1.83-.11a10.318 10.318 0 00-6.32 10.21 10.475 10.475 0 007.04 8.99 10 10 0 002.89.55c.16.01.32.02.48.02a10.5 10.5 0 008.47-4.27c.67-.93.49-1.519.32-1.79z"
      fill="currentColor"
    />
  </svg>
);

export const SunFilledIcon = ({
  size = 24,
  width,
  height,
  ...props
}: IconSvgProps) => (
  <svg
    aria-hidden="true"
    focusable="false"
    height={size || height}
    role="presentation"
    viewBox="0 0 24 24"
    width={size || width}
    {...props}
  >
    <g fill="currentColor">
      <path d="M19 12a7 7 0 11-7-7 7 7 0 017 7z" />
      <path d="M12 22.96a.969.969 0 01-1-.96v-.08a1 1 0 012 0 1.038 1.038 0 01-1 1.04zm7.14-2.82a1.024 1.024 0 01-.71-.29l-.13-.13a1 1 0 011.41-1.41l.13.13a1 1 0 010 1.41.984.984 0 01-.7.29zm-14.28 0a1.024 1.024 0 01-.71-.29 1 1 0 010-1.41l.13-.13a1 1 0 011.41 1.41l-.13.13a1 1 0 01-.7.29zM22 13h-.08a1 1 0 010-2 1.038 1.038 0 011.04 1 .969.969 0 01-.96 1zM2.08 13H2a1 1 0 010-2 1.038 1.038 0 011.04 1 .969.969 0 01-.96 1zm16.93-7.01a1.024 1.024 0 01-.71-.29 1 1 0 010-1.41l.13-.13a1 1 0 011.41 1.41l-.13.13a.984.984 0 01-.7.29zm-14.02 0a1.024 1.024 0 01-.71-.29l-.13-.14a1 1 0 011.41-1.41l.13.13a1 1 0 010 1.41.97.97 0 01-.7.3zM12 3.04a.969.969 0 01-1-.96V2a1 1 0 012 0 1.038 1.038 0 01-1 1.04z" />
    </g>
  </svg>
);

export function TurboLogo() {
  return (
    <svg
      className="fill-zinc-950 dark:fill-zinc-100 "
      viewBox="0 0 1000 173"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M292.267 83.9957V67.4957H228.467V44.5957H211.967V113.496C211.967 127.296 223.167 138.496 236.967 138.496H292.267V121.996H228.467V83.8957H292.267V83.9957Z" />
      <path d="M409.167 67.5957V138.896H393.467L392.667 130.396C392.467 136.896 386.567 138.596 382.167 138.896H327.567C313.767 138.896 302.667 127.696 302.667 113.996V67.5957H319.167V113.996C319.167 118.696 322.967 122.496 327.667 122.496H392.667V67.5957H409.167Z" />
      <path d="M499.766 67.5957V83.9957H444.966C440.266 83.9957 436.466 87.7957 436.466 92.4957V138.896H420.066V67.5957H435.766L436.566 76.0957C436.766 69.5957 442.666 67.8957 447.066 67.5957H499.966H499.766Z" />
      <path d="M595.467 67.5958H524.867V44.6958H508.367V138.896H519.267L520.967 129.696C521.167 135.796 527.067 138.196 531.467 138.796C532.067 138.796 532.667 138.796 533.267 138.796H595.367C606.267 138.796 615.167 129.996 615.167 119.096V87.0958C615.167 76.2958 606.267 67.4958 595.367 67.4958L595.467 67.5958ZM598.767 122.496H524.867V83.9958H598.767V122.496Z" />
      <path d="M705.767 67.5957H649.167C635.367 67.5957 624.167 78.7957 624.167 92.5957V114.196C624.167 127.996 635.367 139.196 649.167 139.196H705.767C719.567 139.196 730.767 127.996 730.767 114.196V92.5957C730.767 78.7957 719.567 67.5957 705.767 67.5957ZM714.267 122.696H640.667V84.0957H714.267V122.696Z" />
      <path d="M829.566 107.196H850.666L840.166 92.4956L829.566 107.196Z" />
      <path d="M917.466 96.1958V93.9958C917.466 91.4958 916.166 90.1958 913.566 90.1958H882.566V100.096H913.566C916.166 100.096 917.466 98.7958 917.466 96.2958V96.1958Z" />
      <path d="M983.367 67.5957H763.867C754.667 67.5957 747.267 74.9957 747.267 84.1957V122.196C747.267 131.396 754.667 138.796 763.867 138.796H983.367C992.567 138.796 999.967 131.396 999.967 122.196V84.1957C999.967 74.9957 992.567 67.5957 983.367 67.5957ZM808.967 122.496H763.667V83.8957H771.267V116.296H808.967V122.596V122.496ZM861.567 122.496L855.167 113.596H825.067L818.667 122.496H809.267L836.867 83.8957H843.167L870.767 122.496H861.467H861.567ZM925.767 116.296C925.267 117.596 924.467 118.696 923.567 119.596C922.667 120.496 921.567 121.196 920.267 121.696C918.967 122.196 917.567 122.496 916.067 122.496H875.067V83.8957H914.567C916.067 83.8957 917.467 84.1957 918.767 84.6957C920.067 85.1957 921.167 85.8957 922.067 86.7957C922.967 87.6957 923.767 88.7957 924.267 90.0957C924.767 91.3957 925.067 92.7957 925.067 94.2957V95.9957C925.067 97.1957 924.867 98.3957 924.567 99.3957C924.167 100.496 923.767 101.396 923.167 102.296C924.167 103.196 924.967 104.296 925.567 105.696C926.167 107.096 926.467 108.596 926.467 110.196V111.996C926.467 113.496 926.267 114.896 925.767 116.196V116.296ZM983.367 111.996C983.367 113.596 983.067 114.996 982.567 116.296C982.067 117.596 981.267 118.696 980.367 119.596C979.467 120.496 978.367 121.196 977.067 121.696C975.767 122.196 974.367 122.496 972.867 122.496H942.767C941.267 122.496 939.867 122.196 938.567 121.696C937.267 121.196 936.167 120.496 935.267 119.596C934.367 118.696 933.667 117.596 933.067 116.296C932.567 114.996 932.267 113.596 932.267 112.096V110.896H939.867V112.396C939.867 114.896 941.167 116.196 943.767 116.196H971.967C974.467 116.196 975.767 114.896 975.767 112.396V108.996C975.767 107.896 975.267 107.196 974.567 106.696C973.867 106.196 972.967 105.996 971.967 105.996H942.767C941.167 105.996 939.867 105.796 938.667 105.296C937.367 104.796 936.267 104.096 935.367 103.196C934.467 102.296 933.667 101.196 933.167 99.9957C932.667 98.6957 932.367 97.3957 932.267 95.7957V94.2957C932.267 92.7957 932.567 91.3957 933.067 90.1957C933.567 88.8957 934.267 87.7957 935.267 86.8957C936.167 85.9957 937.367 85.1957 938.567 84.6957C939.867 84.1957 941.267 83.8957 942.767 83.8957H971.767C973.167 83.8957 974.567 84.0957 975.767 84.5957C977.067 85.0957 978.067 85.6957 979.067 86.5957C979.967 87.3957 980.767 88.4957 981.267 89.5957C981.767 90.7957 982.167 92.0957 982.167 93.5957H974.567C974.567 92.3957 974.067 91.5957 973.467 90.9957C972.767 90.3957 971.867 90.0957 970.767 90.0957H943.767C941.167 90.0957 939.867 91.3957 939.867 93.8957V95.7957C939.867 98.2957 941.167 99.5957 943.767 99.5957H972.867C974.367 99.5957 975.767 99.8957 977.067 100.396C978.367 100.896 979.467 101.596 980.367 102.496C981.267 103.396 982.067 104.496 982.567 105.796C983.067 107.096 983.367 108.496 983.367 109.996V111.996Z" />
      <path d="M915.066 106.196H882.666V116.196H915.066C917.666 116.196 918.966 114.896 918.966 112.396V109.996C918.966 107.496 917.666 106.196 915.066 106.196Z" />
      <path d="M137.523 130.413C152.873 111.368 162.301 90.0966 165.57 67.1667L142.545 107.152C134.918 120.796 124.163 133.446 110.614 144.626L107.582 147.137L166.091 45.5633C167.086 43.8577 167.607 41.9627 167.654 40.0203H167.607V39.2623C168.033 32.9613 164.006 26.2339 158.226 23.6282H158.132C128.664 5.81492 81.4776 -6.31331 36.0441 11.8317H85.1729C99.8121 12.0212 115.02 14.9585 130.37 20.6909L134.065 22.0648H16.4305C14.9145 22.0648 13.4458 22.4912 12.1667 23.2492H12.0719C5.72353 25.7602 1.22283 33.0087 1.88609 39.8308V39.9255C1.60183 51.8643 2.83361 112.505 52.5783 151.685L28.6061 110.042C21.1207 96.9661 15.9094 81.948 13.1616 65.319L12.4983 61.4816L71.2444 163.245C72.2393 164.998 73.7079 166.419 75.4609 167.367H75.5556C80.8617 171.157 89.1051 171.062 94.3638 167.225H94.4586C103.602 162.108 121.368 150.69 137.571 130.603L137.523 130.413ZM91.1423 157.181C89.8157 159.502 87.4469 160.876 84.7939 160.876C82.1408 160.876 79.772 159.502 78.4455 157.181L12.0245 42.1522C10.1295 38.8359 11.4087 35.8986 12.0245 34.809C12.6404 33.7193 14.5355 31.161 18.3729 31.161H151.025C154.863 31.161 156.758 33.7193 157.374 34.809C157.99 35.8986 159.269 38.8359 157.374 42.1522L91.0949 157.181H91.1423Z" />
      <path d="M26.3779 43.1943L84.6977 144.152L143.017 43.1943H26.4253H26.3779ZM79.8653 103.835L81.7129 107.057C82.85 108.999 85.2188 109.994 87.3033 109.331C89.6721 108.62 92.0409 107.673 94.2676 106.583L96.3521 105.588L84.6977 125.771L65.842 92.9864H65.8894C60.2991 82.1373 59.541 69.9143 63.8996 59.1126H60.1096C57.8355 59.1126 55.8931 60.6287 55.372 62.8079C54.8035 65.2715 54.4244 67.8298 54.2349 70.3881L54.0928 72.7095L42.391 52.4326H79.8179C91.6145 52.7169 103.079 58.3546 110.612 67.5929L112.46 64.3714C113.597 62.4289 113.218 59.8706 111.607 58.402C109.759 56.6965 107.817 55.1331 105.733 53.7592L103.837 52.48H127.099L108.007 85.501C101.469 95.4499 91.2355 102.13 79.8653 103.93V103.835Z" />
    </svg>
  );
}

export function TurboISO({ className = "fill-zinc-950 dark:fill-zinc-100", ...props }) {
  return (
    <svg
      className={className}
      viewBox="0 0 168 172"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M137.523 130.413C152.873 111.368 162.301 90.0966 165.57 67.1667L142.545 107.152C134.918 120.796 124.163 133.446 110.614 144.626L107.582 147.137L166.091 45.5633C167.086 43.8577 167.607 41.9627 167.654 40.0203H167.607V39.2623C168.033 32.9613 164.006 26.2339 158.226 23.6282H158.132C128.664 5.81492 81.4776 -6.31331 36.0441 11.8317H85.1729C99.8121 12.0212 115.02 14.9585 130.37 20.6909L134.065 22.0648H16.4305C14.9145 22.0648 13.4458 22.4912 12.1667 23.2492H12.0719C5.72353 25.7602 1.22283 33.0087 1.88609 39.8308V39.9255C1.60183 51.8643 2.83361 112.505 52.5783 151.685L28.6061 110.042C21.1207 96.9661 15.9094 81.948 13.1616 65.319L12.4983 61.4816L71.2444 163.245C72.2393 164.998 73.7079 166.419 75.4609 167.367H75.5556C80.8617 171.157 89.1051 171.062 94.3638 167.225H94.4586C103.602 162.108 121.368 150.69 137.571 130.603L137.523 130.413ZM91.1423 157.181C89.8157 159.502 87.4469 160.876 84.7939 160.876C82.1408 160.876 79.772 159.502 78.4455 157.181L12.0245 42.1522C10.1295 38.8359 11.4087 35.8986 12.0245 34.809C12.6404 33.7193 14.5355 31.161 18.3729 31.161H151.025C154.863 31.161 156.758 33.7193 157.374 34.809C157.99 35.8986 159.269 38.8359 157.374 42.1522L91.0949 157.181H91.1423Z" />
      <path d="M26.3779 43.1943L84.6977 144.152L143.017 43.1943H26.4253H26.3779ZM79.8653 103.835L81.7129 107.057C82.85 108.999 85.2188 109.994 87.3033 109.331C89.6721 108.62 92.0409 107.673 94.2676 106.583L96.3521 105.588L84.6977 125.771L65.842 92.9864H65.8894C60.2991 82.1373 59.541 69.9143 63.8996 59.1126H60.1096C57.8355 59.1126 55.8931 60.6287 55.372 62.8079C54.8035 65.2715 54.4244 67.8298 54.2349 70.3881L54.0928 72.7095L42.391 52.4326H79.8179C91.6145 52.7169 103.079 58.3546 110.612 67.5929L112.46 64.3714C113.597 62.4289 113.218 59.8706 111.607 58.402C109.759 56.6965 107.817 55.1331 105.733 53.7592L103.837 52.48H127.099L108.007 85.501C101.469 95.4499 91.2355 102.13 79.8653 103.93V103.835Z" />
    </svg>
  );
}

export function LinkedInIcon() {
  return (
    <svg
      className="fill-zinc-950 dark:fill-zinc-100"
      viewBox="0 0 448 512"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z" />
    </svg>
  );
}

export function InstagramIcon() {
  return (
    <svg
      className="fill-zinc-950 dark:fill-zinc-100"
      viewBox="0 0 448 512"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
    </svg>
  );
}

export function YoutubeIcon() {
  return (
    <svg
      className="fill-zinc-950 dark:fill-zinc-100"
      viewBox="0 0 576 512"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6-11.4 42.9-11.4 132.3-11.4 132.3s0 89.4 11.4 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8 11.4-42.9 11.4-132.3 11.4-132.3s0-89.4-11.4-132.3zm-317.5 213.5V175.2l142.7 81.2-142.7 81.2z" />
    </svg>
  );
}

export function FacebookIcon() {
  return (
    <svg
      className="fill-zinc-950 dark:fill-zinc-100 max-h-full"
      viewBox="0 0 320 512"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z" />
    </svg>
  );
}

export const SimulationModelingIcon = ({ size = 24, ...props }) => (
  <svg
    height={size}
    viewBox="0 0 32 32"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M22 4v3H10V4H2v8h8V9h7.023A4.95 4.95 0 0 0 16 12v8a3.004 3.004 0 0 1-3 3h-3v-3H2v8h8v-3h3a5.006 5.006 0 0 0 5-5v-8a3 3 0 0 1 2.997-3H22v3h8V4ZM8 10H4V6h4Zm0 16H4v-4h4Zm20-16h-4V6h4Z"
      fill="currentColor"
    />
  </svg>
);

export const OperationsResearchIcon = ({ size = 24, ...props }) => (
  <svg
    height={size}
    viewBox="0 0 32 32"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M27 24a2.96 2.96 0 0 0-1.285.3l-4.3-4.3H18v2h2.586l3.715 3.715A3 3 0 0 0 24 27a3 3 0 1 0 3-3m0 4a1 1 0 1 1 1-1a1 1 0 0 1-1 1m0-15a2.995 2.995 0 0 0-2.816 2H18v2h6.184A2.995 2.995 0 1 0 27 13m0 4a1 1 0 1 1 1-1a1 1 0 0 1-1 1m0-15a3.003 3.003 0 0 0-3 3a3 3 0 0 0 .348 1.373L20.596 10H18v2h3.404l4.4-4.252A2.999 2.999 0 1 0 27 2m0 4a1 1 0 1 1 1-1a1 1 0 0 1-1 1"
      fill="currentColor"
    />
    <path
      d="M18 6h2V4h-2a3.98 3.98 0 0 0-3 1.382A3.98 3.98 0 0 0 12 4h-1a9.01 9.01 0 0 0-9 9v6a9.01 9.01 0 0 0 9 9h1a3.98 3.98 0 0 0 3-1.382A3.98 3.98 0 0 0 18 28h2v-2h-2a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2m-6 20h-1a7.005 7.005 0 0 1-6.92-6H6v-2H4v-4h3a3.003 3.003 0 0 0 3-3V9H8v2a1 1 0 0 1-1 1H4.08A7.005 7.005 0 0 1 11 6h1a2 2 0 0 1 2 2v4h-2v2h2v4h-2a3.003 3.003 0 0 0-3 3v2h2v-2a1 1 0 0 1 1-1h2v4a2 2 0 0 1-2 2"
      fill="currentColor"
    />
  </svg>
);

export const SupplyManagementIcon = ({ size = 24, ...props }) => (
  <svg
    height={size}
    viewBox="0 0 32 32"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M23 23v3H8.5a4.5 4.5 0 0 1 0-9H9v-2h-.5a6.5 6.5 0 0 0 0 13H23v3h8v-8Zm6 6h-4v-4h4Z"
      fill="currentColor"
    />
    <path
      d="M21 22h-2v-3h-6v3h-2v-3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2zm-5-6a3 3 0 1 1 3-3a3.003 3.003 0 0 1-3 3m0-4a1 1 0 1 0 1 1a1 1 0 0 0-1-1"
      fill="currentColor"
    />
    <path
      d="M23.5 4H9V1H1v8h8V6h14.5a4.5 4.5 0 0 1 0 9H23v2h.5a6.5 6.5 0 0 0 0-13M7 7H3V3h4Z"
      fill="currentColor"
    />
  </svg>
);

export const SchedulingOptimizationIcon = ({ size = 24, ...props }) => (
  <svg
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <g
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M9 20H6a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4h11a4 4 0 0 1 4 4v3M8 2v2m7-2v2M2 8h19m-2.5 7.644l-1.5 1.5" />
      <circle cx="17" cy="17" r="5" />
    </g>
  </svg>
);

export const HarnessingAIIcon = ({ size = 24, ...props }) => (
  <svg
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M3.993 14.999V6.965c0-1.894 0-2.84.583-3.429.326-.328.762-.473 1.41-.537h4.98M3.492 16.014L4.012 15h15.873l.548 1.015c1.438 2.662 1.796 3.993 1.25 4.989-.547.996-1.996.996-4.893.996H7.135c-2.897 0-4.346 0-4.892-.996S1.188 18.346 2.493 15.014M15.436 2.001V3.98M12.436 3.533h2.045M18.382 3.533H20m-2.045 2.974H20m-10.038 0h2.045M13.42 4.998v2m3.014-1.98v2M18.44 2v1.98m-3.437 5.999h3.956a1 1 0 0 0 .997-1V4.98a1 1 0 0 0-.997-1h-3.956a1 1 0 0 0-.997 1v3.999c0 .552.446 1 .997 1"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

export const ProcessAutomationIcon = ({ size = 24, ...props }) => (
  <svg
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M17.5 15.5c0 1.11-.89 2-2 2s-2-.89-2-2s.9-2 2-2s2 .9 2 2m-9-2c-1.1 0-2 .9-2 2s.9 2 2 2s2-.89 2-2s-.89-2-2-2M23 15v3c0 .55-.45 1-1 1h-1v1c0 1.11-.89 2-2 2H5a2 2 0 0 1-2-2v-1H2c-.55 0-1-.45-1-1v-3c0-.55.45-1 1-1h1c0-3.87 3.13-7 7-7h1V5.73c-.6-.34-1-.99-1-1.73c0-1.1.9-2 2-2s2 .9 2 2c0 .74-.4 1.39-1 1.73V7h1c3.87 0 7 3.13 7 7h1c.55 0 1 .45 1 1m-2 1h-2v-2c0-2.76-2.24-5-5-5h-4c-2.76 0-5 2.24-5 5v2H3v1h2v3h14v-3h2z"
      fill="currentColor"
    />
  </svg>
);
